import { IModel, IAttribute, Collection, IViolation, IValidationContext, ModelConstants } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { Dataset } from 'app/model/dataset/dataset';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { ParameterValue } from 'app/model/base/parameter-value';
import { validateEntityParameters, validateCopySource, validateSourceDataset, validateManagedVNetIRStatus } from 'app/shared/utils/activity-validation.util';
import { format } from 'app/shared/utils/string.util';

@Metamodel.model({
    name: ModelConstants.lookupActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.LookupLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.LookupLabel)
    ],
    referable: true
})
export class LookupActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly FIRST_ROW_ONLY: IAttribute;

    /**
     * Returns first row or all rows.
     *
     *      firstRowOnly is a tristate value of undefined, true & false
     *      and firstRowOnly would be activated when it's either undefined or true,
     *      firstRowOnly would be deactivated only when it's false.
     *
     *      in other world, firstRowOnly is by default activated even it is undefined
     */
    @Metamodel.attribute({
        display: stringResources.LookupActivityConfigurationFirstRowOnly,
        association: 'FIRST_ROW_ONLY',
        templatable: true
    })
    public firstRowOnly?: boolean;

    @Metamodel.attribute({
        display: !__IS_FABRIC_DPE__ ? stringResources.ModelName_DataSet : stringResources.ConnectionLabel,
        constraint: {
            required: true,
            validator: LookupActivity.validateSourceDataset
        },
        reference: (context) => !(context?.inlineDataset),
        type: Dataset.MODEL
    })
    public sourceDataset?: Dataset;

    @Metamodel.attribute({
        display: stringResources.SourceActivity_SourceTab_SourceType,
        constraint: {
            required: true,
            validator: LookupActivity.validateSource
        },
        reference: false,
        type: CopySource.MODEL
    })
    public source?: CopySource;

    /**
     * Parameter value for referenced source dataset.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false,
            validator: LookupActivity.validateSourceDatasetParameters
        }
    })
    public sourceDatasetParameters: ParameterValue[] = [];

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static validateSourceDatasetParameters(activity: LookupActivity): IViolation[] | undefined {
        if (activity != null && activity.sourceDataset != null) {
            return validateEntityParameters(activity.sourceDataset, activity.sourceDatasetParameters || [], activity);
        }
        return undefined;
    }

    private static async validateSource(activity: LookupActivity, _value: CopySource, _violations: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        return await validateCopySource(activity, activity.source, activity.sourceDataset, undefined, context);
    }

    private static async validateSourceDataset(activity: LookupActivity, _value: Dataset, _violations: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        let result: IViolation[] = [];
        const irStatusViolations = await validateManagedVNetIRStatus(context, activity.sourceDataset);
        if (irStatusViolations != null) {
            result = result.concat(irStatusViolations);
        }
        const datasetViolation = await validateSourceDataset(activity, activity.sourceDataset, false, false, false, context);
        if (datasetViolation != null) {
            result = result.concat(datasetViolation);
        }
        return result;
    }

    public onActivityCloned(clonedActivity: LookupActivity, context: { inlineDataset?: boolean }): void {
        if (this.sourceDataset != null) {
            this.sourceDataset.onDatasetCloned(clonedActivity.sourceDataset, context);
        }
    }
}
